import type { ReadableStore, Store, ViewportSize } from '@frontend/viewer-core';

import type { HistoryStore } from './historyStore';
import { attachInputController } from './inputController';
import type { PointerTracker } from './pointerTracker';
import type {
  RoleChromeManager,
  DrawingModel,
  MappingManager,
  MarqueeController,
  MarqueePreview,
  MeasureManager,
  SelectionManager,
  ToolId,
  ToolManager,
  ViewerMode,
  ViewportManager,
} from './types';

type Dependencies = {
  sizeStore: Store<ViewportSize>;
  /**
   * Drawing currently on screen. Read + subscribed so the one-shot fit can wait
   * for geometry (size alone is not enough — see `fitOnceMeasured`).
   */
  model: ReadableStore<DrawingModel | null>;
  getTool: () => ToolId;
  tools: Pick<ToolManager, 'armBrush' | 'set'>;
  mapping: Pick<MappingManager, 'assignAndClear'>;
  roleChrome: Pick<RoleChromeManager, 'open' | 'close' | 'dismiss' | 'release'>;
  getMode: () => ViewerMode;
  getIsPaused: () => boolean;
  history: Pick<HistoryStore, 'undo' | 'redo'>;
  viewport: ViewportManager;
  selection: SelectionManager;
  marqueeController: MarqueeController;
  marqueePreview: ReadableStore<MarqueePreview | null>;
  measure: MeasureManager;
  pointerTracker: Pick<PointerTracker, 'setFrom'>;
};

/**
 * Binds the ResizeObserver and input listeners to the render surface.
 *
 * Returns a function matching `DxfViewerSession.attachSurface` exactly, so the
 * composition root only has to guard re-entrancy and hand the host through.
 */
export const createSurfaceBinding =
  ({
    sizeStore,
    model,
    getTool,
    tools,
    mapping,
    roleChrome,
    getMode,
    getIsPaused,
    history,
    viewport,
    selection,
    marqueeController,
    marqueePreview,
    measure,
    pointerTracker,
  }: Dependencies) =>
  (host: HTMLElement): (() => void) => {
    const measureInto = () => {
      const { width, height } = host.getBoundingClientRect();
      sizeStore.setState((previous) =>
        previous.width === width && previous.height === height
          ? previous
          : { width, height },
      );
    };

    const observer = new ResizeObserver(measureInto);
    observer.observe(host);

    const detachInput = attachInputController(host, {
      viewport,
      selection,
      marquee: marqueeController,
      marqueePreview,
      measure,
      tool: getTool,
      tools,
      mapping,
      getModel: model.getState,
      roleChrome,
      mode: getMode,
      isPaused: getIsPaused,
      history,
      setPointer: pointerTracker.setFrom,
    });

    // `loadScene` can run before this surface is attached, so the first
    // `viewport.reset`/`fit` may land against a zero-size container. `fitAabb`
    // then keeps scale=1 — wrong zoom for most drawings. Re-fit exactly once
    // both size and geometry exist; later resizes deliberately do NOT.
    //
    // Do not latch on size alone: measuring below can populate the store before
    // any subscribe runs, and a prior fit may have used width=0. Do not latch
    // while `model` is null either — that would skip the post-scene re-fit and
    // would also re-fit on a later resize after `sceneLoader` had already framed.
    let hasFittedWhenMeasured = false;
    const fitOnceMeasured = () => {
      if (hasFittedWhenMeasured || sizeStore.getState().width <= 0) {
        return;
      }
      if (!model.getState()) {
        return;
      }
      hasFittedWhenMeasured = true;
      viewport.fit();
    };

    measureInto();
    fitOnceMeasured();
    const unsubscribeSize = sizeStore.subscribe(fitOnceMeasured);
    const unsubscribeModel = model.subscribe(fitOnceMeasured);

    return () => {
      observer.disconnect();
      detachInput();
      unsubscribeSize();
      unsubscribeModel();
    };
  };
